<template>
    <v-dialog v-model="modalProp" width="500">
        <v-card>
            <v-card-title class="headline grey lighten-2">
                Change Address
                <v-spacer />
                <v-btn
                    icon
                    @click="modalProp = false"
                >
                    <v-icon>{{ mdiClose }}</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="mt-6">
                <v-text-field
                    v-model="newAddr_1"
                    label="Address Line 1 (Blk/Lt/Unit/Tower)"
                    color="green accent-4"
                    autocomplete="nope"
                />

                <v-text-field
                    v-model="newAddr_2"
                    label="Address Line 2 (Street/Subd./Condo)"
                    color="green accent-4"
                    autocomplete="nope"
                />

                <v-text-field
                    v-model="newBarangay"
                    label="Barangay"
                    color="green accent-4"
                    autocomplete="nope"
                />

                <v-text-field
                    v-model="newDistrict"
                    label="District"
                    color="green accent-4"
                    autocomplete="nope"
                />

                <v-text-field
                    v-model="newZip"
                    v-mask="zipMask"
                    label="ZIP Code"
                    :rules="rules.zipRule"
                    color="green accent-4"
                    autocomplete="nope"
                />

                <!-- Regions -->
                <v-autocomplete
                    v-model="regionObj"
                    color="green accent-4"
                    label="Region"
                    :items="getRegions"
                    item-text="city_region"
                    item-value="city_region"
                    return-object
                    autocomplete="nope"
                    @change="handleChangeRegion($event)"
                />

                <!-- Provinces -->
                <v-autocomplete
                    v-model="provinceObj"
                    color="green accent-4"
                    label="Province"
                    :items="provinceItems"
                    item-text="city_province"
                    item-value="uid"
                    return-object
                    autocomplete="nope"
                    @change="handleChangeProvince($event)"
                >
                    <template #item="{ item }">
                        <v-list-item-content>
                            <v-list-item-title>{{ item.city_province }}</v-list-item-title>
                            <v-list-item-subtitle>{{ item.city_region }}</v-list-item-subtitle>
                        </v-list-item-content>
                    </template>
                </v-autocomplete>

                <!-- Cities -->
                <v-autocomplete
                    v-model="cityObj"
                    color="green accent-4"
                    label="City"
                    :items="cityItems"
                    item-text="city_name"
                    item-value="uid"
                    return-object
                    autocomplete="nope"
                    @change="handleChangeCity($event)"
                >
                    <template #item="{ item }">
                        <v-list-item-content>
                            <v-list-item-title>{{ item.city_name }}</v-list-item-title>
                            <v-list-item-subtitle>{{ item.city_region }}, {{ item.city_province }}</v-list-item-subtitle>
                        </v-list-item-content>
                    </template>
                </v-autocomplete>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-spacer />
                <v-btn color="green" text @click="updateAddress()">
                    Update
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

  <script>
import { mapActions, mapGetters } from 'vuex'
import { mask } from 'vue-the-mask'
import { mdiClose } from '@mdi/js'
export default {
    directives: {
        mask
    },
    props: ['editAddress', 'userAddr_1', 'userAddr_2', 'userBarangay', 'userDistrict', 'userCity', 'userProvince', 'userRegion', 'userZip'],
    data: () => ({
        mdiClose,
        region: '',
        province: '',
        city: '',
        regionObj: null,
        provinceObj: null,
        cityObj: null,
        rules: {
            fieldRule: [
                (validation) => !!validation || 'Field is required.'
            ],
            zipRule: [
                (validation) => /^$/.test(validation) || /^\d{4}$/.test(validation) || 'Invalid ZIP Code'
            ]
        },
        zipMask: '####',
        newAddr_1: '',
        newAddr_2: '',
        newBarangay: '',
        newDistrict: '',
        newZip: ''
    }),
    computed: {
        modalProp: {
            get() {
                return this.editAddress
            },
            set(val) {
                this.$emit('showDlgEditAddress', val)
            }
        },
        ...mapGetters(['getRegions', 'getProvinces', 'getCities']),
        provinceItems() {
            return (this.getProvinces || []).map(item => ({
                ...item,
                uid: `${item.city_region}${item.city_province}`
            }))
        },
        cityItems() {
            return (this.getCities || []).map(item => ({
                ...item,
                uid: `${item.city_region}${item.city_province}${item.city_name}`
            }))
        }
    },
    watch: {
        // resolve pre-existing user values into objects once each list arrives
        getRegions() {
            if (this.region && !this.regionObj) {
                this.regionObj = (this.getRegions || []).find(i => i.city_region === this.region) || null
            }
        },
        getProvinces() {
            if (this.province && !this.provinceObj) {
                this.provinceObj = this.provinceItems.find(i => i.city_province === this.province) || null
            }
        },
        getCities() {
            if (this.city && !this.cityObj) {
                this.cityObj = this.cityItems.find(i => i.city_name === this.city) || null
            }
        }
    },
    async created() {
        // keep prefilling the existing address values so editing still shows them
        this.region = this.userRegion
        this.province = this.userProvince
        this.city = this.userCity
        this.newAddr_1 = this.userAddr_1
        this.newAddr_2 = this.userAddr_2
        this.newBarangay = this.userBarangay
        this.newDistrict = this.userDistrict
        this.newZip = this.userZip

        this.regions()
        // load full/unfiltered lists by default so City/Province can be
        // picked first; if the user already has a saved region, scope
        // the initial lists to it instead
        this.provinces({ REGION: this.region ? this.region : '' })
        this.cities({ REGION: this.region ? this.region : '', PROVINCE: this.province ? this.province : '' })
    },
    methods: {
        ...mapActions(['manageAccountAddress', 'regions', 'provinces', 'cities', 'getCsrfToken']),
        handleChangeRegion(item) {
            if (!item) {
                this.region = ''
                this.province = ''
                this.city = ''
                this.provinceObj = null
                this.cityObj = null
                this.provinces({ REGION: '' })
                this.cities({ REGION: '', PROVINCE: '' })
                return
            }

            this.region = item.city_region

            // if the currently selected province/city no longer belongs to
            // the newly picked region, clear them since they're now invalid
            if (this.provinceObj && this.provinceObj.city_region !== item.city_region) {
                this.province = ''
                this.provinceObj = null
            }
            if (this.cityObj && this.cityObj.city_region !== item.city_region) {
                this.city = ''
                this.cityObj = null
            }

            // region only filters the lists; it does not require province
            // to be picked before city
            this.provinces({ REGION: item.city_region })
            this.cities({ REGION: item.city_region, PROVINCE: this.province ? this.province : '' })
        },
        handleChangeProvince(item) {
            if (!item) {
                this.province = ''
                // fall back city list to whatever region is still selected
                this.cities({ REGION: this.region ? this.region : '', PROVINCE: '' })
                return
            }

            this.province = item.city_province
            this.region = item.city_region
            this.regionObj = (this.getRegions || []).find(i => i.city_region === item.city_region) || this.regionObj

            // if the currently selected city doesn't belong to this province, clear it
            if (this.cityObj && (this.cityObj.city_region !== item.city_region || this.cityObj.city_province !== item.city_province)) {
                this.city = ''
                this.cityObj = null
            }

            // province narrows the city list further
            this.cities({ REGION: item.city_region, PROVINCE: item.city_province })
        },
        handleChangeCity(item) {
            if (!item) {
                this.city = ''
                return
            }

            this.city = item.city_name
            this.province = item.city_province
            this.region = item.city_region
            this.regionObj = (this.getRegions || []).find(i => i.city_region === item.city_region) || this.regionObj
            this.provinceObj = this.provinceItems.find(i => i.uid === `${item.city_region}${item.city_province}`) || this.provinceObj

            // city is terminal — no further filtering needed, but refresh
            // the province list scope so it's consistent if the user backtracks
            this.provinces({ REGION: item.city_region })
        },
        async updateAddress() {
            this.manageAccountAddress({
                MEMBN: this.$cookies.get('usermembn'),
                NEW_ADDRESS: {
                    ADDR_1: this.newAddr_1,
                    ADDR_2: this.newAddr_2,
                    BARANGAY: this.newBarangay,
                    DISTRICT: this.newDistrict,
                    CITY: this.city,
                    PROVINCE: this.province,
                    REGION: this.region,
                    ZIP: this.newZip
                }
            }).then(() => {
                this.modalProp = false
            }).then(() => {
                window.location.reload()
            })
        }
    }
}
  </script>

  <style></style>
